/**
 * AgentOrchestration - AI-SDR Agent Pipeline Visualization
 *
 * Shows how multiple AI agents work together to automate
 * sales development and outreach:
 *
 * 1. Supervisor Agent - Orchestrates campaign execution
 * 2. Company Research Agent - Discovers target companies
 * 3. Lead Research Agent - Identifies decision-makers
 * 4. Enrichment Agents - LinkedIn, Apollo, Google data
 * 5. Email Agent - Generates personalized sequences
 * 6. Telephone Agent - Voice outreach and voicemail
 * 
 * All agents powered by Gemini 3.0 Pro Preview
 */

import React, { useState, useEffect } from "react";
import {
  FaRobot,
  FaBrain,
  FaPlay,
  FaDatabase,
  FaCheckCircle,
  FaUserTie,
  FaArrowRight,
  FaCog,
  FaNetworkWired,
  FaEye,
  FaProjectDiagram,
  FaBuilding,
  FaUsers,
  FaEnvelope,
  FaPhone,
  FaLinkedin,
  FaMapMarkerAlt,
  FaSearch,
  FaChartLine,
  FaCalendarCheck,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";

// Mermaid diagram component
const MermaidDiagram = ({ chart, isDark }) => {
  const [svgContent, setSvgContent] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderMermaid = async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? "dark" : "default",
          themeVariables: isDark
            ? {
                primaryColor: "#6366f1",
                primaryTextColor: "#fff",
                primaryBorderColor: "#4f46e5",
                lineColor: "#64748b",
                secondaryColor: "#1e293b",
                tertiaryColor: "#0f172a",
                background: "#1e293b",
                mainBkg: "#1e293b",
                nodeBorder: "#475569",
                clusterBkg: "#1e293b",
                titleColor: "#f1f5f9",
                edgeLabelBackground: "#1e293b",
              }
            : {
                primaryColor: "#6366f1",
                primaryTextColor: "#1e293b",
                primaryBorderColor: "#4f46e5",
                lineColor: "#94a3b8",
                secondaryColor: "#f1f5f9",
                tertiaryColor: "#ffffff",
                background: "#ffffff",
                mainBkg: "#ffffff",
                nodeBorder: "#e2e8f0",
                clusterBkg: "#f8fafc",
                titleColor: "#1e293b",
                edgeLabelBackground: "#ffffff",
              },
          flowchart: {
            curve: "basis",
            padding: 20,
          },
        });

        const uniqueId = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(uniqueId, chart);
        setSvgContent(svg);
      } catch (err) {
        console.error("Mermaid rendering error:", err);
        setError(err.message);
      }
    };

    renderMermaid();
  }, [chart, isDark]);

  if (error) {
    return (
      <div className={`p-4 rounded-xl text-sm ${
        isDark 
          ? "bg-red-500/10 border border-red-500/30 text-red-400" 
          : "bg-red-50 border border-red-200 text-red-600"
      }`}>
        Diagram rendering error: {error}
      </div>
    );
  }

  return (
    <div
      className="mermaid-container overflow-auto"
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

const AgentOrchestration = () => {
  const isDark = false; // AI-SDR Agents uses light theme only
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeView, setActiveView] = useState("pipeline");

  const agents = [
    {
      id: "supervisor",
      name: "Supervisor Agent",
      icon: FaUserTie,
      color: "indigo",
      description:
        "Orchestrates the entire campaign, decides when to trigger outreach, monitors goals",
      responsibilities: [
        "Coordinates all agent activities",
        "Manages campaign state & timing",
        "Monitors meeting goal completion",
        "Handles stop conditions & retries",
      ],
      model: "Gemini 3.0 Pro Preview",
      status: "active",
    },
    {
      id: "company-research",
      name: "Company Research Agent",
      icon: FaBuilding,
      color: "blue",
      description:
        "Discovers target companies using Google Maps, web scraping, and industry databases",
      responsibilities: [
        "Proximity search via Google Maps API",
        "Industry classification & filtering",
        "Company size & revenue analysis",
        "Waste profile identification",
      ],
      model: "Gemini 3.0 Pro Preview",
      status: "active",
    },
    {
      id: "lead-research",
      name: "Lead Research Agent",
      icon: FaUsers,
      color: "purple",
      description:
        "Identifies decision-makers within target companies for outreach",
      responsibilities: [
        "Find Sustainability Directors",
        "Locate Facility Managers",
        "Identify VP Operations",
        "Discover Procurement leads",
      ],
      model: "Gemini 3.0 Pro Preview",
      status: "active",
    },
    {
      id: "enrichment",
      name: "Data Enrichment Agents",
      icon: FaDatabase,
      color: "cyan",
      description:
        "LinkedIn, Apollo.io, and Google Search agents enrich lead data",
      responsibilities: [
        "LinkedIn Sales Navigator lookup",
        "Apollo.io email/phone enrichment",
        "Google Search web intelligence",
        "Confidence scoring & validation",
      ],
      model: "Gemini 3.0 Pro Preview",
      status: "active",
    },
    {
      id: "email",
      name: "Email Agent",
      icon: FaEnvelope,
      color: "green",
      description:
        "Generates personalized 5-touch email sequences using AI",
      responsibilities: [
        "AI-powered email generation",
        "5-touch cadence (Day 0,3,6,10,14)",
        "Personalization per lead/role",
        "Reply detection & stop logic",
      ],
      model: "Gemini 3.0 Pro Preview",
      status: "active",
    },
    {
      id: "telephone",
      name: "Telephone Agent",
      icon: FaPhone,
      color: "amber",
      description:
        "Handles voice outreach via Twilio with voicemail and real-time Gemini 3.0 voice AI",
      responsibilities: [
        "Twilio call initiation",
        "Voicemail script generation",
        "Real-time voice AI (Gemini 3.0)",
        "Call outcome tracking",
      ],
      model: "Gemini 3.0 Pro Preview",
      status: "active",
    },
  ];

  // Main Pipeline Diagram
  const pipelineDiagram = `
flowchart TB
    subgraph INPUT["🎯 TARGET IDENTIFICATION"]
        COL[("Collectors<br/>ReWorld Locations")]
        GEN[("Generators<br/>Waste Producers")]
    end
    
    subgraph AGENTS["🤖 AI AGENT ORCHESTRATION"]
        direction TB
        SUP[["🎯 Supervisor Agent<br/>Campaign Controller"]]
        
        subgraph RESEARCH["Research Phase"]
            CR[["🏢 Company Research<br/>Google Maps + Web"]]
            LR[["👥 Lead Research<br/>Decision-Maker ID"]]
        end
        
        subgraph ENRICHMENT["Enrichment Phase"]
            LN[["💼 LinkedIn Agent"]]
            AP[["📊 Apollo.io Agent"]]
            GS[["🔍 Google Search Agent"]]
        end
        
        subgraph OUTREACH["Outreach Phase"]
            EA[["📧 Email Agent<br/>5-Touch Sequence"]]
            TA[["📞 Telephone Agent<br/>3 Call Attempts"]]
        end
    end
    
    subgraph OUTPUT["🏆 CAMPAIGN GOALS"]
        MEET[("Meeting<br/>Booked")]
        EXHAUST[("Touchpoints<br/>Exhausted")]
    end
    
    COL --> SUP
    GEN --> SUP
    
    SUP --> CR
    CR --> LR
    LR --> LN
    LR --> AP
    LR --> GS
    
    LN --> EA
    AP --> EA
    GS --> EA
    
    EA --> TA
    
    EA -->|Reply/Meeting| MEET
    TA -->|Meeting Set| MEET
    TA -->|No Response| EXHAUST
    
    style INPUT fill:#fef3c7,stroke:#f59e0b
    style AGENTS fill:#dbeafe,stroke:#3b82f6
    style OUTPUT fill:#d1fae5,stroke:#10b981
    style SUP fill:#6366f1,stroke:#4f46e5,color:#fff
    style CR fill:#3b82f6,stroke:#2563eb,color:#fff
    style LR fill:#8b5cf6,stroke:#7c3aed,color:#fff
    style EA fill:#10b981,stroke:#059669,color:#fff
    style TA fill:#f59e0b,stroke:#d97706,color:#fff
`;

  // Outreach Sequence Diagram
  const outreachDiagram = `
sequenceDiagram
    participant S as Supervisor
    participant E as Email Agent
    participant T as Telephone Agent
    participant L as Lead
    
    Note over S,L: Campaign Start
    
    S->>E: Day 0 - Spark Email
    E->>L: Send personalized email
    
    S->>T: Day 2 - Intro Call
    T->>L: Call attempt #1
    
    S->>E: Day 3 - Value Add Email
    E->>L: Case study + video
    
    S->>E: Day 6 - Pain/Insight Email
    E->>L: Hidden costs question
    
    S->>T: Day 7 - Follow-up Call
    T->>L: Call attempt #2
    
    S->>E: Day 10 - Social Proof Email
    E->>L: Peer outcome + calendar
    
    S->>T: Day 12 - Urgency Call
    T->>L: Call attempt #3
    
    S->>E: Day 14 - Break-up Email
    E->>L: Final outreach
    
    alt Meeting Booked
        L-->>S: Meeting confirmed
        Note over S: STOP - Goal achieved
    else No Response
        Note over S: Move to next lead
    end
`;

  // Data Flow Diagram
  const dataFlowDiagram = `
flowchart LR
    subgraph SOURCES["Data Sources"]
        GM[Google Maps API]
        LI[LinkedIn]
        AP[Apollo.io]
        WEB[Web Search]
    end
    
    subgraph STORE["Firestore Database"]
        direction TB
        CO[(companies/)]
        LE[(leads/)]
        CA[(campaigns/)]
        CT[(contacts/)]
    end
    
    subgraph OUTREACH["Outreach Services"]
        OAI[Gemini 3.0 Pro]
        TW[Twilio + Gemini 3.0]
        SG[SendGrid Email]
    end
    
    GM --> CO
    LI --> LE
    AP --> CT
    WEB --> CO
    
    CO --> CA
    LE --> CA
    CT --> CA
    
    CA --> OAI
    OAI --> SG
    CA --> TW
    
    style SOURCES fill:#fef3c7,stroke:#f59e0b
    style STORE fill:#dbeafe,stroke:#3b82f6
    style OUTREACH fill:#d1fae5,stroke:#10b981
`;

  const AgentCard = ({ agent, isSelected, onClick }) => {
    const colorMapDark = {
      indigo: { bg: "bg-indigo-500/20", border: "border-indigo-500/30", text: "text-indigo-400" },
      blue: { bg: "bg-blue-500/20", border: "border-blue-500/30", text: "text-blue-400" },
      purple: { bg: "bg-purple-500/20", border: "border-purple-500/30", text: "text-purple-400" },
      cyan: { bg: "bg-cyan-500/20", border: "border-cyan-500/30", text: "text-cyan-400" },
      green: { bg: "bg-green-500/20", border: "border-green-500/30", text: "text-green-400" },
      amber: { bg: "bg-amber-500/20", border: "border-amber-500/30", text: "text-amber-400" },
    };
    const colorMapLight = {
      indigo: { bg: "bg-indigo-100", border: "border-indigo-300", text: "text-indigo-600" },
      blue: { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-600" },
      purple: { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-600" },
      cyan: { bg: "bg-cyan-100", border: "border-cyan-300", text: "text-cyan-600" },
      green: { bg: "bg-green-100", border: "border-green-300", text: "text-green-600" },
      amber: { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-600" },
    };
    const colors = isDark ? colorMapDark[agent.color] : colorMapLight[agent.color];
    const Icon = agent.icon;

    return (
      <div
        onClick={() => onClick(agent)}
        className={`p-4 rounded-xl cursor-pointer transition-all ${
          isSelected
            ? `${colors.bg} ${colors.border} border-2`
            : isDark 
              ? "bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800" 
              : "bg-white border border-gray-200 hover:bg-gray-50 shadow-sm"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={colors.text} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className={`font-semibold text-sm ${isDark ? "text-white" : "text-gray-900"}`}>{agent.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                agent.status === "active" 
                  ? isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"
                  : isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-600"
              }`}>
                {agent.status}
              </span>
            </div>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{agent.description}</p>
            <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] ${
              isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-700"
            }`}>
              <HiSparkles className="text-[8px]" />
              {agent.model}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`p-6 min-h-screen ${isDark ? "bg-slate-900 text-white" : "bg-white text-gray-900"}`}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className={`text-3xl font-bold flex items-center gap-3 ${isDark ? "text-white" : "text-gray-900"}`}>
            <FaRobot className="text-indigo-500" />
            AI-SDR Agent Orchestration
          </h1>
        </div>
        <p className={isDark ? "text-slate-400" : "text-gray-600"}>
          How AI agents work together to automate sales development for ReWorld
        </p>
      </div>

      {/* View Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl mb-6 inline-flex ${isDark ? "bg-slate-800" : "bg-gray-200"}`}>
        {[
          { id: "pipeline", label: "Full Pipeline", icon: FaProjectDiagram },
          { id: "outreach", label: "Outreach Sequence", icon: FaCalendarCheck },
          { id: "dataflow", label: "Data Flow", icon: FaDatabase },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeView === tab.id
                ? isDark 
                  ? "bg-indigo-500/20 text-indigo-400" 
                  : "bg-white text-indigo-600 shadow-sm"
                : isDark 
                  ? "text-slate-400 hover:text-white" 
                  : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <tab.icon className="text-xs" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main Diagram */}
      <div className={`rounded-2xl p-6 mb-8 ${
        isDark ? "bg-slate-800/50 border border-slate-700/50" : "bg-white border border-gray-200 shadow-sm"
      }`}>
        <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
          <FaProjectDiagram className="text-indigo-500" />
          {activeView === "pipeline" && "Complete SDR Pipeline"}
          {activeView === "outreach" && "5 Email + 3 Call Outreach Sequence"}
          {activeView === "dataflow" && "Data Flow Architecture"}
        </h2>
        <div className={`p-4 rounded-xl overflow-auto ${isDark ? "bg-slate-900/50" : "bg-gray-50"}`}>
          {activeView === "pipeline" && <MermaidDiagram chart={pipelineDiagram} isDark={isDark} />}
          {activeView === "outreach" && <MermaidDiagram chart={outreachDiagram} isDark={isDark} />}
          {activeView === "dataflow" && <MermaidDiagram chart={dataFlowDiagram} isDark={isDark} />}
        </div>
      </div>

      {/* Process Flow Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className={`p-5 rounded-2xl ${isDark ? "bg-blue-500/10 border border-blue-500/30" : "bg-blue-50 border border-blue-200"}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}>
              <FaBuilding className={isDark ? "text-blue-400" : "text-blue-600"} />
            </div>
            <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>1. Research</h3>
          </div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>Discover companies & leads</p>
          <ul className={`text-xs mt-2 space-y-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
            <li>• Google Maps proximity</li>
            <li>• LinkedIn lookup</li>
            <li>• Apollo.io enrichment</li>
          </ul>
        </div>

        <div className={`p-5 rounded-2xl ${isDark ? "bg-purple-500/10 border border-purple-500/30" : "bg-purple-50 border border-purple-200"}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-purple-500/20" : "bg-purple-100"}`}>
              <FaDatabase className={isDark ? "text-purple-400" : "text-purple-600"} />
            </div>
            <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>2. Enrich</h3>
          </div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>Verify & enhance data</p>
          <ul className={`text-xs mt-2 space-y-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
            <li>• Email verification</li>
            <li>• Phone validation</li>
            <li>• Confidence scoring</li>
          </ul>
        </div>

        <div className={`p-5 rounded-2xl ${isDark ? "bg-green-500/10 border border-green-500/30" : "bg-green-50 border border-green-200"}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-green-500/20" : "bg-green-100"}`}>
              <FaEnvelope className={isDark ? "text-green-400" : "text-green-600"} />
            </div>
            <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>3. Email</h3>
          </div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>5-touch email sequence</p>
          <ul className={`text-xs mt-2 space-y-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
            <li>• Day 0, 3, 6, 10, 14</li>
            <li>• AI personalization</li>
            <li>• Reply detection</li>
          </ul>
        </div>

        <div className={`p-5 rounded-2xl ${isDark ? "bg-amber-500/10 border border-amber-500/30" : "bg-amber-50 border border-amber-200"}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-amber-500/20" : "bg-amber-100"}`}>
              <FaPhone className={isDark ? "text-amber-400" : "text-amber-600"} />
            </div>
            <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>4. Call</h3>
          </div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>3 phone attempts</p>
          <ul className={`text-xs mt-2 space-y-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
            <li>• Day 2, 7, 12</li>
            <li>• Voicemail drops</li>
            <li>• Real-time voice AI</li>
          </ul>
        </div>
      </div>

      {/* Campaign Goals */}
      <div className={`rounded-2xl p-4 mb-8 ${isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
            <FaCalendarCheck className={isDark ? "text-emerald-400" : "text-emerald-600"} />
          </div>
          <div>
            <h3 className={`font-semibold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>Campaign Goals</h3>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
              <strong>5 emails + 3 calls</strong> before moving on. Auto-stop if meeting is booked or reply received.
            </p>
          </div>
        </div>
      </div>

      {/* Agents Grid */}
      <div className={`rounded-2xl ${isDark ? "bg-slate-800/50 border border-slate-700/50" : "bg-white border border-gray-200 shadow-sm"}`}>
        <div className={`p-5 border-b ${isDark ? "border-slate-700/50" : "border-gray-200"}`}>
          <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
            <FaRobot className="text-indigo-500" />
            AI Agents in the Pipeline
            <span className={`ml-2 px-2 py-0.5 rounded text-[10px] ${isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-700"}`}>
              Powered by Gemini 3.0 Pro Preview
            </span>
          </h2>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              isSelected={selectedAgent?.id === agent.id}
              onClick={setSelectedAgent}
            />
          ))}
        </div>
      </div>

      {/* Technology Stack */}
      <div className={`rounded-2xl p-6 mt-6 ${
        isDark 
          ? "bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30" 
          : "bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200"
      }`}>
        <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
          <FaNetworkWired className="text-indigo-500" />
          Technology Stack
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`p-4 rounded-xl ${isDark ? "bg-slate-900/50" : "bg-white shadow-sm"}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded flex items-center justify-center ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}>
                <FaMapMarkerAlt className={`text-sm ${isDark ? "text-blue-400" : "text-blue-600"}`} />
              </div>
              <span className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}>Google Maps API</span>
            </div>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Proximity search & geocoding</p>
          </div>

          <div className={`p-4 rounded-xl ${isDark ? "bg-slate-900/50" : "bg-white shadow-sm"}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded flex items-center justify-center ${isDark ? "bg-purple-500/20" : "bg-purple-100"}`}>
                <FaLinkedin className={`text-sm ${isDark ? "text-purple-400" : "text-purple-600"}`} />
              </div>
              <span className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}>LinkedIn + Apollo</span>
            </div>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Lead enrichment & contact data</p>
          </div>

          <div className={`p-4 rounded-xl ${isDark ? "bg-slate-900/50" : "bg-white shadow-sm"}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded flex items-center justify-center ${isDark ? "bg-green-500/20" : "bg-green-100"}`}>
                <HiSparkles className={`text-sm ${isDark ? "text-green-400" : "text-green-600"}`} />
              </div>
              <span className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}>Gemini 3.0 Pro</span>
            </div>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Email generation & voice AI</p>
          </div>

          <div className={`p-4 rounded-xl ${isDark ? "bg-slate-900/50" : "bg-white shadow-sm"}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded flex items-center justify-center ${isDark ? "bg-amber-500/20" : "bg-amber-100"}`}>
                <FaPhone className={`text-sm ${isDark ? "text-amber-400" : "text-amber-600"}`} />
              </div>
              <span className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}>Twilio + Gemini 3.0</span>
            </div>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Real-time voice AI calls</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentOrchestration;
